import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Buildings, Users, Package, CreditCard, ChartBar, CaretRight, MagnifyingGlass,
  ArrowLeft, CheckCircle, Clock, XCircle, Crown, ShieldCheck, SignOut,
  PencilSimple, Key, Eye, Trash, ChatCircleDots, Megaphone, ArrowClockwise,
  Headset, EnvelopeSimple, Phone, UserCircle, Copy, Globe, Bell, BellRinging,
  Article, Plus, Robot, ToggleLeft, ToggleRight
} from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const sa = () => {
  const token = localStorage.getItem('sa_token');
  return { headers: { Authorization: `Bearer ${token}` } };
};

const STATUS_COLORS = {
  active: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', label: 'Active' },
  pending: { bg: 'bg-amber-500/10', text: 'text-amber-400', label: 'Pending' },
  suspended: { bg: 'bg-red-500/10', text: 'text-red-400', label: 'Suspended' },
  cancelled: { bg: 'bg-gray-500/10', text: 'text-gray-400', label: 'Cancelled' },
};

const PLAN_COLORS = {
  starter: { bg: 'bg-blue-500/10', text: 'text-blue-400' },
  pro: { bg: 'bg-[#FF9900]/10', text: 'text-[#FF9900]' },
  enterprise: { bg: 'bg-purple-500/10', text: 'text-purple-400' },
};

export default function SuperAdminDashboard() {
  const navigate = useNavigate();
  const [view, setView] = useState('overview');
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [companyTotal, setCompanyTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [filterPlan, setFilterPlan] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState({ leads: [], contacts: [], subscribers: [] });
  const [supportChats, setSupportChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatReply, setChatReply] = useState('');
  const [announcements, setAnnouncements] = useState([]);
  const [announcementForm, setAnnouncementForm] = useState({ title: '', message: '', target: 'all' });
  const [editCompany, setEditCompany] = useState(null);
  const [resetPwUser, setResetPwUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [notifications, setNotifications] = useState({ unread_support: 0, new_leads_today: 0, latest_unread_messages: [] });
  const lastNotifRef = useRef('');
  const notifSoundRef = useRef(null);
  // Blog management
  const [blogPosts, setBlogPosts] = useState([]);
  const [blogStats, setBlogStats] = useState(null);
  const [blogTotal, setBlogTotal] = useState(0);
  const [blogPage, setBlogPage] = useState(1);
  const [blogPages, setBlogPages] = useState(1);
  const [editingPost, setEditingPost] = useState(null);
  const [blogGenerating, setBlogGenerating] = useState(false);
  const [blogGenTopic, setBlogGenTopic] = useState('');
  const [blogGenCategory, setBlogGenCategory] = useState('');
  const [manualPostForm, setManualPostForm] = useState({ title: '', content: '', excerpt: '', category: 'General', tags: '', published: true });

  useEffect(() => {
    const token = localStorage.getItem('sa_token');
    if (!token) { navigate('/super-admin'); return; }
    loadOverview();
    if (Notification.permission === 'default') Notification.requestPermission();
  }, [navigate]);

  // Poll for notifications every 5 seconds
  useEffect(() => {
    if (loading) return;
    const poll = setInterval(async () => {
      try {
        const res = await axios.get(`${API_URL}/api/super-admin/notifications`, sa());
        const data = res.data;
        setNotifications(data);

        // Browser notification for new support messages
        if (data.latest_unread_messages?.length > 0) {
          const latestKey = data.latest_unread_messages.map(m => m.created_at).join(',');
          if (latestKey !== lastNotifRef.current) {
            lastNotifRef.current = latestKey;
            data.latest_unread_messages.forEach(msg => {
              if (Notification.permission === 'granted') {
                new Notification(`Support: ${msg.company_name}`, {
                  body: msg.message,
                  icon: '/assets/trackmypackage-logo.png',
                  tag: msg.chat_id,
                });
              }
              toast.info(`New message from ${msg.company_name}: "${msg.message.substring(0, 60)}..."`, { duration: 6000 });
            });
            // Play sound
            try {
              const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbsGczGjF+xN/QZi8RFVB0nb65ZDcjLWqRxMVgMBcaS3ORw8NVKRITQ26OuLBOHhIVQmyIq6pFGhITOF+AoaM+FhAQMVN4mZo0EA8QKkxtiJUuDg4PLjpSYo2WHg4dKT5BXIqlIBAYGS0tMTlAUImTGxoiMUZJJCUlJTlQYJGXHB4iNUdNLCooNUJUMJqfICA2UmYwpKIiNlFmNKyjJDhTaDSopig8WGw4sKssQF5wPLOwMElkcESwtDRLaHRItLw4UXR8ULzESGF8iGDM1Fx1kKBw3OBojaCscOjseLGwuIT5BIjFwMiRCRCY0dDYnRkgpOHc5KkpMLD17PSxQUC9BgUMxVVQ0RYRFNFlYOEmHS0A=');
              audio.volume = 0.3;
              audio.play().catch(() => {});
            } catch (e) {}
          }
        }

        // Refresh support chats if on support tab
        if (data.unread_support > 0) {
          const chatsRes = await axios.get(`${API_URL}/api/super-admin/support-chats`, sa()).catch(() => ({ data: [] }));
          setSupportChats(chatsRes.data);
          // Auto-refresh current chat if open
          if (selectedChat) {
            const msgRes = await axios.get(`${API_URL}/api/super-admin/support-chats/${selectedChat}/messages`, sa()).catch(() => ({ data: [] }));
            setChatMessages(msgRes.data);
          }
        }
      } catch (err) {}
    }, 5000);
    return () => clearInterval(poll);
  }, [loading]);

  const loadOverview = async () => {
    try {
      const [statsRes, companiesRes, leadsRes, chatsRes, annRes] = await Promise.all([
        axios.get(`${API_URL}/api/super-admin/overview`, sa()),
        axios.get(`${API_URL}/api/super-admin/companies/list`, sa()),
        axios.get(`${API_URL}/api/super-admin/leads`, sa()),
        axios.get(`${API_URL}/api/super-admin/support-chats`, sa()).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/super-admin/announcements`, sa()).catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setCompanies(companiesRes.data.companies);
      setCompanyTotal(companiesRes.data.total);
      setLeads(leadsRes.data);
      setSupportChats(chatsRes.data);
      setAnnouncements(annRes.data);
    } catch (err) {
      if (err.response?.status === 401) { localStorage.removeItem('sa_token'); navigate('/super-admin'); }
      else toast.error('Failed to load data');
    } finally { setLoading(false); }
  };

  const searchCompanies = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (filterPlan) params.set('plan', filterPlan);
      if (filterStatus) params.set('status', filterStatus);
      const res = await axios.get(`${API_URL}/api/super-admin/companies/list?${params}`, sa());
      setCompanies(res.data.companies);
      setCompanyTotal(res.data.total);
    } catch (err) { toast.error('Search failed'); }
  }, [search, filterPlan, filterStatus]);

  useEffect(() => { if (!loading) searchCompanies(); }, [search, filterPlan, filterStatus, searchCompanies, loading]);

  const loadCompanyDetail = async (companyId) => {
    try {
      const res = await axios.get(`${API_URL}/api/super-admin/companies/${companyId}`, sa());
      setSelectedCompany(res.data);
      setView('company-detail');
    } catch (err) { toast.error('Failed to load company'); }
  };

  const handleUpdateCompany = async () => {
    if (!editCompany) return;
    try {
      await axios.put(`${API_URL}/api/super-admin/companies/${selectedCompany.id}`, editCompany, sa());
      toast.success('Company updated');
      setEditCompany(null);
      loadCompanyDetail(selectedCompany.id);
      loadOverview();
    } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
  };

  const handleImpersonate = async (companyId) => {
    try {
      const res = await axios.post(`${API_URL}/api/super-admin/impersonate/${companyId}`, {}, sa());
      localStorage.setItem('token', res.data.access_token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      toast.success(`Logged in as ${res.data.user.name || res.data.user.email}`);
      window.open('/dashboard', '_blank');
    } catch (err) { toast.error(err.response?.data?.detail || 'Impersonation failed'); }
  };

  const handleResetPassword = async () => {
    if (!resetPwUser || !newPassword) return;
    try {
      await axios.put(`${API_URL}/api/super-admin/users/${resetPwUser.id}/reset-password`, { new_password: newPassword }, sa());
      toast.success(`Password reset for ${resetPwUser.email}`);
      setResetPwUser(null);
      setNewPassword('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Reset failed'); }
  };

  const handleStatusChange = async (companyId, status) => {
    try {
      await axios.put(`${API_URL}/api/super-admin/companies/${companyId}`, { subscription_status: status }, sa());
      toast.success(`Status changed to ${status}`);
      loadCompanyDetail(companyId);
      loadOverview();
    } catch (err) { toast.error('Status change failed'); }
  };

  const handleToggleSupport = async (companyId, current) => {
    try {
      await axios.put(`${API_URL}/api/super-admin/companies/${companyId}`, { support_addon: !current }, sa());
      toast.success(!current ? 'Support add-on activated' : 'Support add-on deactivated');
      loadCompanyDetail(companyId);
      loadOverview();
    } catch (err) { toast.error('Failed to toggle support'); }
  };

  const loadChatMessages = async (chatId) => {
    try {
      const res = await axios.get(`${API_URL}/api/super-admin/support-chats/${chatId}/messages`, sa());
      setChatMessages(res.data);
      setSelectedChat(chatId);
      // Mark as read
      await axios.post(`${API_URL}/api/super-admin/support-chats/${chatId}/mark-read`, {}, sa()).catch(() => {});
      setSupportChats(prev => prev.map(c => c.id === chatId ? { ...c, has_unread_admin: false } : c));
    } catch (err) { toast.error('Failed to load messages'); }
  };

  const sendChatReply = async () => {
    if (!chatReply.trim() || !selectedChat) return;
    try {
      await axios.post(`${API_URL}/api/super-admin/support-chats/${selectedChat}/reply`, { content: chatReply }, sa());
      setChatReply('');
      loadChatMessages(selectedChat);
    } catch (err) { toast.error('Send failed'); }
  };

  const sendAnnouncement = async () => {
    if (!announcementForm.title || !announcementForm.message) return;
    try {
      await axios.post(`${API_URL}/api/super-admin/announcements`, announcementForm, sa());
      toast.success('Announcement sent');
      setAnnouncementForm({ title: '', message: '', target: 'all' });
      const res = await axios.get(`${API_URL}/api/super-admin/announcements`, sa());
      setAnnouncements(res.data);
    } catch (err) { toast.error('Failed to send announcement'); }
  };

  const handleLogout = () => {
    localStorage.removeItem('sa_token');
    localStorage.removeItem('sa_user');
    navigate('/super-admin');
  };

  // Blog management functions
  const loadBlogData = async () => {
    try {
      const [postsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/blog/admin/posts?page=${blogPage}&limit=20`, sa()),
        axios.get(`${API_URL}/api/blog/admin/stats`, sa()),
      ]);
      setBlogPosts(postsRes.data.posts);
      setBlogTotal(postsRes.data.total);
      setBlogPages(postsRes.data.pages);
      setBlogStats(statsRes.data);
    } catch (err) { toast.error('Failed to load blog data'); }
  };

  useEffect(() => { if (view === 'blog') loadBlogData(); }, [view, blogPage]);

  const handleGenerateArticle = async () => {
    setBlogGenerating(true);
    try {
      const body = {};
      if (blogGenTopic) body.topic = blogGenTopic;
      if (blogGenCategory) body.category = blogGenCategory;
      const res = await axios.post(`${API_URL}/api/blog/admin/generate`, body, sa());
      toast.success(`Generated: "${res.data.title}"`);
      setBlogGenTopic('');
      setBlogGenCategory('');
      loadBlogData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Generation failed'); }
    setBlogGenerating(false);
  };

  const handleTogglePublish = async (post) => {
    try {
      await axios.put(`${API_URL}/api/blog/admin/posts/${post.id}`, { published: !post.published }, sa());
      toast.success(post.published ? 'Unpublished' : 'Published');
      loadBlogData();
    } catch (err) { toast.error('Update failed'); }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this article permanently?')) return;
    try {
      await axios.delete(`${API_URL}/api/blog/admin/posts/${postId}`, sa());
      toast.success('Article deleted');
      loadBlogData();
    } catch (err) { toast.error('Delete failed'); }
  };

  const handleSaveEdit = async () => {
    if (!editingPost) return;
    try {
      const update = {
        title: editingPost.title,
        content: editingPost.content,
        excerpt: editingPost.excerpt,
        meta_description: editingPost.meta_description,
        category: editingPost.category,
        tags: editingPost.tags,
        published: editingPost.published,
      };
      await axios.put(`${API_URL}/api/blog/admin/posts/${editingPost.id}`, update, sa());
      toast.success('Article updated');
      setEditingPost(null);
      loadBlogData();
    } catch (err) { toast.error('Save failed'); }
  };

  const handleCreateManualPost = async () => {
    if (!manualPostForm.title || !manualPostForm.content) { toast.error('Title and content required'); return; }
    try {
      const body = { ...manualPostForm, tags: manualPostForm.tags.split(',').map(t => t.trim()).filter(Boolean) };
      await axios.post(`${API_URL}/api/blog/admin/create`, body, sa());
      toast.success('Article created');
      setManualPostForm({ title: '', content: '', excerpt: '', category: 'General', tags: '', published: true });
      loadBlogData();
    } catch (err) { toast.error('Create failed'); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#060B15] flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
    </div>
  );

  const MetricCard = ({ icon: Icon, label, value, sub, color = 'text-white' }) => (
    <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4">
      <div className="flex items-center gap-3 mb-2">
        <Icon size={20} className="text-gray-500" />
        <span className="text-xs text-gray-500 uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#060B15] text-white" data-testid="super-admin-dashboard">
      {/* Top bar */}
      <div className="border-b border-white/10 bg-white/[0.02] px-6 py-3 flex items-center justify-between sticky top-0 z-50 backdrop-blur-md">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-red-600/10 rounded-lg flex items-center justify-center border border-red-600/20">
            <ShieldCheck size={18} className="text-red-500" weight="duotone" />
          </div>
          <span className="font-bold text-sm tracking-wide">Super Admin</span>
          <span className="text-[10px] text-gray-600">Track My Package</span>
        </div>
        <div className="flex items-center gap-2">
          {['overview', 'companies', 'leads', 'support', 'announcements', 'blog'].map(tab => (
            <button
              key={tab}
              onClick={() => { setView(tab); setSelectedCompany(null); }}
              className={`relative px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${view === tab || (view === 'company-detail' && tab === 'companies') ? 'bg-red-600/10 text-red-400' : 'text-gray-500 hover:text-white'}`}
              data-testid={`sa-tab-${tab}`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              {tab === 'support' && notifications.unread_support > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-[9px] font-bold text-white flex items-center justify-center animate-pulse" data-testid="support-badge">
                  {notifications.unread_support}
                </span>
              )}
            </button>
          ))}
          {/* Notification Bell */}
          <div className="relative ml-2">
            {notifications.unread_support > 0 ? (
              <BellRinging size={18} className="text-red-400 animate-bounce" weight="duotone" />
            ) : (
              <Bell size={18} className="text-gray-600" />
            )}
            {(notifications.unread_support + notifications.new_leads_today) > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 bg-red-500 rounded-full text-[8px] font-bold text-white flex items-center justify-center">
                {notifications.unread_support + notifications.new_leads_today}
              </span>
            )}
          </div>
          <button onClick={handleLogout} className="ml-3 text-gray-500 hover:text-red-400 transition-colors" data-testid="sa-logout-btn">
            <SignOut size={18} />
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* OVERVIEW */}
        {view === 'overview' && stats && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Bebas Neue', letterSpacing: '0.05em' }}>Platform Overview</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-8">
              <MetricCard icon={Buildings} label="Companies" value={stats.companies.total} sub={`${stats.companies.active} active`} />
              <MetricCard icon={Users} label="Users" value={stats.users} />
              <MetricCard icon={Package} label="Packages" value={stats.packages} />
              <MetricCard icon={CreditCard} label="MRR" value={`$${stats.mrr.toLocaleString()}`} color="text-emerald-400" />
              <MetricCard icon={Headset} label="Support Subs" value={stats.support_addon_subscribers} sub="$99/mo add-on" />
              <MetricCard icon={ChartBar} label="Invoices" value={stats.invoices} sub={`$${stats.revenue_usd.toLocaleString()} revenue`} />
              <MetricCard icon={ChatCircleDots} label="Leads" value={stats.leads} />
              <MetricCard icon={EnvelopeSimple} label="Contacts" value={stats.contacts} />
              <MetricCard icon={Users} label="Customers" value={stats.customers} />
              <MetricCard icon={Crown} label="Plans" value={Object.entries(stats.companies.plan_distribution).map(([k,v]) => `${k}: ${v}`).join(', ')} />
            </div>
            <div className="bg-white/[0.03] border border-white/10 rounded-xl p-5">
              <h3 className="font-bold mb-4 text-sm">Recent Companies</h3>
              <div className="space-y-2">
                {companies.slice(0, 8).map(c => (
                  <div key={c.id} onClick={() => loadCompanyDetail(c.id)} className="flex items-center justify-between p-3 rounded-lg hover:bg-white/[0.03] cursor-pointer transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-[#FF9900]/10 rounded-lg flex items-center justify-center text-xs font-bold text-[#FF9900]">{c.name.charAt(0)}</div>
                      <div>
                        <p className="text-sm font-medium">{c.name}</p>
                        <p className="text-[10px] text-gray-500">{c.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${(PLAN_COLORS[c.plan] || PLAN_COLORS.starter).bg} ${(PLAN_COLORS[c.plan] || PLAN_COLORS.starter).text}`}>{c.plan}</span>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${(STATUS_COLORS[c.subscription_status] || STATUS_COLORS.pending).bg} ${(STATUS_COLORS[c.subscription_status] || STATUS_COLORS.pending).text}`}>{c.subscription_status}</span>
                      {c.support_addon && <Headset size={14} className="text-emerald-400" />}
                      <CaretRight size={14} className="text-gray-600" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* COMPANIES LIST */}
        {view === 'companies' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Bebas Neue', letterSpacing: '0.05em' }}>All Companies ({companyTotal})</h2>
            <div className="flex flex-wrap gap-3 mb-5">
              <div className="relative flex-1 min-w-[200px]">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email..." className="pl-9 bg-white/5 border-white/10 text-white rounded-xl h-10" data-testid="sa-search" />
              </div>
              <select value={filterPlan} onChange={e => setFilterPlan(e.target.value)} className="bg-white/5 border border-white/10 text-white text-xs rounded-xl px-3 h-10" data-testid="sa-filter-plan">
                <option value="">All Plans</option>
                <option value="starter">Starter</option>
                <option value="pro">Professional</option>
                <option value="enterprise">Enterprise</option>
              </select>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="bg-white/5 border border-white/10 text-white text-xs rounded-xl px-3 h-10" data-testid="sa-filter-status">
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
            <div className="bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10 text-gray-500 text-[10px] uppercase tracking-wider">
                    <th className="text-left p-3">Company</th>
                    <th className="text-left p-3">Plan</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-center p-3">Clients</th>
                    <th className="text-center p-3">Packages</th>
                    <th className="text-center p-3">Support</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map(c => (
                    <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 bg-[#FF9900]/10 rounded-lg flex items-center justify-center text-[10px] font-bold text-[#FF9900]">{c.name.charAt(0)}</div>
                          <div>
                            <p className="font-medium text-xs">{c.name}</p>
                            <p className="text-[10px] text-gray-500">{c.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-3"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${(PLAN_COLORS[c.plan] || PLAN_COLORS.starter).bg} ${(PLAN_COLORS[c.plan] || PLAN_COLORS.starter).text}`}>{c.plan}</span></td>
                      <td className="p-3"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${(STATUS_COLORS[c.subscription_status] || STATUS_COLORS.pending).bg} ${(STATUS_COLORS[c.subscription_status] || STATUS_COLORS.pending).text}`}>{c.subscription_status}</span></td>
                      <td className="p-3 text-center text-xs text-gray-400">{c.customers}</td>
                      <td className="p-3 text-center text-xs text-gray-400">{c.packages}</td>
                      <td className="p-3 text-center">{c.support_addon ? <Headset size={14} className="text-emerald-400 mx-auto" /> : <span className="text-[10px] text-gray-600">-</span>}</td>
                      <td className="p-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => loadCompanyDetail(c.id)} className="p-1.5 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white" title="View" data-testid={`sa-view-${c.id}`}><Eye size={14} /></button>
                          <button onClick={() => handleImpersonate(c.id)} className="p-1.5 rounded-lg hover:bg-blue-500/10 text-gray-400 hover:text-blue-400" title="Impersonate" data-testid={`sa-impersonate-${c.id}`}><UserCircle size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* COMPANY DETAIL */}
        {view === 'company-detail' && selectedCompany && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <button onClick={() => setView('companies')} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white mb-4" data-testid="sa-back-btn">
              <ArrowLeft size={16} /> Back to companies
            </button>

            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold" style={{ fontFamily: 'Bebas Neue', letterSpacing: '0.05em' }}>{selectedCompany.name}</h2>
                <p className="text-sm text-gray-500">{selectedCompany.email} {selectedCompany.phone ? `| ${selectedCompany.phone}` : ''}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={() => handleImpersonate(selectedCompany.id)} variant="outline" className="rounded-xl text-xs border-blue-500/20 text-blue-400 hover:bg-blue-500/10" data-testid="sa-impersonate-btn">
                  <UserCircle size={14} className="mr-1" /> Impersonate
                </Button>
                <Button onClick={() => setEditCompany({ plan: selectedCompany.plan, subscription_status: selectedCompany.subscription_status, name: selectedCompany.name, support_addon: selectedCompany.support_addon || false })} variant="outline" className="rounded-xl text-xs border-white/10 text-white hover:bg-white/10" data-testid="sa-edit-company-btn">
                  <PencilSimple size={14} className="mr-1" /> Edit
                </Button>
              </div>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <MetricCard icon={Users} label="Customers" value={selectedCompany.stats?.customers || 0} />
              <MetricCard icon={Package} label="Packages" value={selectedCompany.stats?.packages || 0} />
              <MetricCard icon={CreditCard} label="Invoices" value={selectedCompany.stats?.invoices || 0} />
              <MetricCard icon={ChartBar} label="Revenue" value={`$${selectedCompany.stats?.revenue_usd || 0}`} color="text-emerald-400" />
            </div>

            {/* Info Cards */}
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4">
                <h3 className="font-bold text-sm mb-3">Subscription</h3>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between"><span className="text-gray-500">Plan</span><span className={`font-semibold ${(PLAN_COLORS[selectedCompany.plan] || {}).text}`}>{selectedCompany.plan}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Status</span><span className={`font-semibold ${(STATUS_COLORS[selectedCompany.subscription_status] || {}).text}`}>{selectedCompany.subscription_status}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Max Clients</span><span>{selectedCompany.max_clients}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Max Users</span><span>{selectedCompany.max_users}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Support Add-on</span><span className={selectedCompany.support_addon ? 'text-emerald-400 font-semibold' : 'text-gray-600'}>{selectedCompany.support_addon ? 'Active' : 'Inactive'}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Created</span><span>{new Date(selectedCompany.created_at).toLocaleDateString()}</span></div>
                </div>
                <div className="flex gap-2 mt-4">
                  {selectedCompany.subscription_status !== 'active' && (
                    <Button onClick={() => handleStatusChange(selectedCompany.id, 'active')} className="text-[10px] h-7 rounded-lg bg-emerald-600 hover:bg-emerald-700" data-testid="sa-activate-btn">Activate</Button>
                  )}
                  {selectedCompany.subscription_status !== 'suspended' && (
                    <Button onClick={() => handleStatusChange(selectedCompany.id, 'suspended')} variant="outline" className="text-[10px] h-7 rounded-lg border-red-500/20 text-red-400 hover:bg-red-500/10" data-testid="sa-suspend-btn">Suspend</Button>
                  )}
                  <Button onClick={() => handleToggleSupport(selectedCompany.id, selectedCompany.support_addon)} variant="outline" className={`text-[10px] h-7 rounded-lg ${selectedCompany.support_addon ? 'border-red-500/20 text-red-400' : 'border-emerald-500/20 text-emerald-400'}`} data-testid="sa-toggle-support">
                    <Headset size={12} className="mr-1" />
                    {selectedCompany.support_addon ? 'Remove Support' : 'Add Support'}
                  </Button>
                </div>
              </div>

              <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4">
                <h3 className="font-bold text-sm mb-3">Users ({selectedCompany.users?.length || 0})</h3>
                <div className="space-y-2">
                  {(selectedCompany.users || []).map(u => (
                    <div key={u.id} className="flex items-center justify-between p-2 rounded-lg bg-white/[0.02]">
                      <div>
                        <p className="text-xs font-medium">{u.name || u.email}</p>
                        <p className="text-[10px] text-gray-500">{u.email} | {u.role}</p>
                      </div>
                      <button onClick={() => { setResetPwUser(u); setNewPassword(''); }} className="p-1.5 rounded-lg hover:bg-amber-500/10 text-gray-500 hover:text-amber-400" title="Reset Password" data-testid={`sa-reset-pw-${u.id}`}>
                        <Key size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Recent Packages */}
            {selectedCompany.packages?.length > 0 && (
              <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4 mb-4">
                <h3 className="font-bold text-sm mb-3">Recent Packages</h3>
                <div className="space-y-1">
                  {selectedCompany.packages.slice(0, 10).map((p, i) => (
                    <div key={i} className="flex items-center justify-between text-xs p-2 rounded-lg hover:bg-white/[0.02]">
                      <span className="font-mono text-gray-400">{p.tracking_number}</span>
                      <span>{p.description || '-'}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${p.status === 'delivered' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}`}>{p.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Edit Modal */}
            <AnimatePresence>
              {editCompany && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setEditCompany(null)}>
                  <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} onClick={e => e.stopPropagation()} className="bg-[#0D1321] border border-white/10 rounded-2xl p-6 w-full max-w-md" data-testid="sa-edit-modal">
                    <h3 className="font-bold text-lg mb-4">Edit Company</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Name</label>
                        <Input value={editCompany.name || ''} onChange={e => setEditCompany(p => ({...p, name: e.target.value}))} className="bg-white/5 border-white/10 text-white rounded-lg" />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Plan</label>
                        <select value={editCompany.plan || ''} onChange={e => setEditCompany(p => ({...p, plan: e.target.value}))} className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-lg p-2">
                          <option value="starter">Starter</option>
                          <option value="pro">Professional</option>
                          <option value="enterprise">Enterprise</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Status</label>
                        <select value={editCompany.subscription_status || ''} onChange={e => setEditCompany(p => ({...p, subscription_status: e.target.value}))} className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-lg p-2">
                          <option value="active">Active</option>
                          <option value="pending">Pending</option>
                          <option value="suspended">Suspended</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </div>
                      <div className="flex items-center gap-2">
                        <input type="checkbox" checked={editCompany.support_addon || false} onChange={e => setEditCompany(p => ({...p, support_addon: e.target.checked}))} id="support-check" className="rounded" />
                        <label htmlFor="support-check" className="text-xs">24/7 Support Add-on ($99/mo)</label>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-5">
                      <Button onClick={handleUpdateCompany} className="bg-red-600 hover:bg-red-700 text-white rounded-xl flex-1" data-testid="sa-save-edit">Save Changes</Button>
                      <Button onClick={() => setEditCompany(null)} variant="outline" className="rounded-xl border-white/10" data-testid="sa-cancel-edit">Cancel</Button>
                    </div>
                  </motion.div>
                </motion.div>
              )}

              {/* Reset Password Modal */}
              {resetPwUser && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setResetPwUser(null)}>
                  <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} onClick={e => e.stopPropagation()} className="bg-[#0D1321] border border-white/10 rounded-2xl p-6 w-full max-w-sm" data-testid="sa-reset-pw-modal">
                    <h3 className="font-bold text-lg mb-2">Reset Password</h3>
                    <p className="text-xs text-gray-500 mb-4">{resetPwUser.email}</p>
                    <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="New password (min 6 chars)" className="bg-white/5 border-white/10 text-white rounded-lg mb-4" data-testid="sa-new-pw-input" />
                    <div className="flex gap-2">
                      <Button onClick={handleResetPassword} className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl flex-1" data-testid="sa-reset-pw-btn">Reset Password</Button>
                      <Button onClick={() => setResetPwUser(null)} variant="outline" className="rounded-xl border-white/10">Cancel</Button>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}

        {/* LEADS */}
        {view === 'leads' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Bebas Neue', letterSpacing: '0.05em' }}>Leads & Contacts</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4">
                <h3 className="font-bold text-sm mb-3 flex items-center gap-2"><ChatCircleDots size={16} className="text-blue-400" /> Chat Leads ({leads.leads?.length || 0})</h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {(leads.leads || []).map((l, i) => (
                    <div key={i} className="p-2 rounded-lg bg-white/[0.02] text-xs">
                      <p className="font-medium">{l.name || 'Anonymous'}</p>
                      <p className="text-gray-500">{l.email}</p>
                      <p className="text-[10px] text-gray-600">{new Date(l.created_at).toLocaleDateString()}</p>
                    </div>
                  ))}
                  {(leads.leads || []).length === 0 && <p className="text-xs text-gray-600">No chat leads yet</p>}
                </div>
              </div>
              <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4">
                <h3 className="font-bold text-sm mb-3 flex items-center gap-2"><EnvelopeSimple size={16} className="text-amber-400" /> Contact Forms ({leads.contacts?.length || 0})</h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {(leads.contacts || []).map((c, i) => (
                    <div key={i} className="p-2 rounded-lg bg-white/[0.02] text-xs">
                      <p className="font-medium">{c.name}</p>
                      <p className="text-gray-500">{c.email} | {c.company}</p>
                      <p className="text-gray-400 mt-1">{c.message?.substring(0, 80)}...</p>
                      <p className="text-[10px] text-gray-600">{new Date(c.created_at).toLocaleDateString()}</p>
                    </div>
                  ))}
                  {(leads.contacts || []).length === 0 && <p className="text-xs text-gray-600">No contacts yet</p>}
                </div>
              </div>
              <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4">
                <h3 className="font-bold text-sm mb-3 flex items-center gap-2"><Package size={16} className="text-emerald-400" /> Track Subscribers ({leads.subscribers?.length || 0})</h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {(leads.subscribers || []).map((s, i) => (
                    <div key={i} className="p-2 rounded-lg bg-white/[0.02] text-xs">
                      <p className="text-gray-500">{s.email}</p>
                      <p className="font-mono text-[10px] text-gray-400">{s.tracking_number}</p>
                    </div>
                  ))}
                  {(leads.subscribers || []).length === 0 && <p className="text-xs text-gray-600">No subscribers yet</p>}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* SUPPORT CHATS */}
        {view === 'support' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Bebas Neue', letterSpacing: '0.05em' }}>Support Chats</h2>
            <div className="grid md:grid-cols-3 gap-4" style={{ height: 'calc(100vh - 200px)' }}>
              {/* Chat list */}
              <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4 overflow-y-auto">
                <h3 className="font-bold text-sm mb-3">Active Chats</h3>
                {supportChats.length === 0 && <p className="text-xs text-gray-600">No support chats yet. Companies with the $99/mo support add-on will appear here.</p>}
                {supportChats.map(chat => (
                  <div key={chat.id} onClick={() => loadChatMessages(chat.id)} className={`p-3 rounded-lg cursor-pointer transition-colors mb-2 ${selectedChat === chat.id ? 'bg-red-600/10 border border-red-600/20' : 'hover:bg-white/[0.03]'}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium">{chat.company_name}</p>
                      {chat.has_unread_admin && <div className="w-2 h-2 bg-red-500 rounded-full"></div>}
                    </div>
                    <p className="text-[10px] text-gray-500">{new Date(chat.updated_at).toLocaleString()}</p>
                  </div>
                ))}
              </div>
              {/* Messages */}
              <div className="md:col-span-2 bg-white/[0.03] border border-white/10 rounded-xl flex flex-col">
                {selectedChat ? (
                  <>
                    <div className="flex-1 p-4 overflow-y-auto space-y-3">
                      {chatMessages.map(msg => (
                        <div key={msg.id} className={`flex ${msg.sender === 'admin' ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[70%] p-3 rounded-xl text-xs ${msg.sender === 'admin' ? 'bg-red-600/10 text-red-200' : 'bg-white/[0.05] text-gray-300'}`}>
                            <p className="text-[10px] text-gray-500 mb-1">{msg.sender_name || msg.sender}</p>
                            <p>{msg.content}</p>
                          </div>
                        </div>
                      ))}
                      {chatMessages.length === 0 && <p className="text-center text-xs text-gray-600 mt-10">No messages yet</p>}
                    </div>
                    <div className="p-3 border-t border-white/10 flex gap-2">
                      <Input value={chatReply} onChange={e => setChatReply(e.target.value)} placeholder="Type your reply..." className="bg-white/5 border-white/10 text-white rounded-xl flex-1" onKeyDown={e => e.key === 'Enter' && sendChatReply()} data-testid="sa-chat-reply" />
                      <Button onClick={sendChatReply} className="bg-red-600 hover:bg-red-700 rounded-xl" data-testid="sa-chat-send">Send</Button>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-gray-600 text-sm">Select a chat to view messages</div>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* ANNOUNCEMENTS */}
        {view === 'announcements' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Bebas Neue', letterSpacing: '0.05em' }}>Announcements</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white/[0.03] border border-white/10 rounded-xl p-5">
                <h3 className="font-bold text-sm mb-4">Send New Announcement</h3>
                <div className="space-y-3">
                  <Input value={announcementForm.title} onChange={e => setAnnouncementForm(p => ({...p, title: e.target.value}))} placeholder="Title" className="bg-white/5 border-white/10 text-white rounded-lg" data-testid="sa-announce-title" />
                  <textarea value={announcementForm.message} onChange={e => setAnnouncementForm(p => ({...p, message: e.target.value}))} placeholder="Message..." rows={4} className="w-full bg-white/5 border border-white/10 text-white rounded-lg p-3 text-sm resize-none" data-testid="sa-announce-message" />
                  <select value={announcementForm.target} onChange={e => setAnnouncementForm(p => ({...p, target: e.target.value}))} className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-lg p-2">
                    <option value="all">All Companies</option>
                    <option value="starter">Starter Plans Only</option>
                    <option value="pro">Pro Plans Only</option>
                    <option value="enterprise">Enterprise Only</option>
                  </select>
                  <Button onClick={sendAnnouncement} className="w-full bg-red-600 hover:bg-red-700 rounded-xl" data-testid="sa-send-announce">
                    <Megaphone size={16} className="mr-2" /> Send Announcement
                  </Button>
                </div>
              </div>
              <div className="bg-white/[0.03] border border-white/10 rounded-xl p-5">
                <h3 className="font-bold text-sm mb-4">Sent Announcements</h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {announcements.map(a => (
                    <div key={a.id} className="p-3 rounded-lg bg-white/[0.02] border border-white/5">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm font-medium">{a.title}</p>
                        <span className="text-[10px] text-gray-500 px-2 py-0.5 rounded-full bg-white/5">{a.target}</span>
                      </div>
                      <p className="text-xs text-gray-400">{a.message}</p>
                      <p className="text-[10px] text-gray-600 mt-1">{new Date(a.created_at).toLocaleString()}</p>
                    </div>
                  ))}
                  {announcements.length === 0 && <p className="text-xs text-gray-600">No announcements sent yet</p>}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* BLOG MANAGEMENT */}
        {view === 'blog' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Bebas Neue', letterSpacing: '0.05em' }}>Blog Management</h2>

            {/* Stats Row */}
            {blogStats && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                <div className="bg-white/[0.03] border border-white/10 rounded-xl p-3">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider">Total Articles</p>
                  <p className="text-xl font-bold text-white">{blogStats.total}</p>
                </div>
                <div className="bg-white/[0.03] border border-white/10 rounded-xl p-3">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider">Published</p>
                  <p className="text-xl font-bold text-emerald-400">{blogStats.published}</p>
                </div>
                <div className="bg-white/[0.03] border border-white/10 rounded-xl p-3">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider">Drafts</p>
                  <p className="text-xl font-bold text-amber-400">{blogStats.drafts}</p>
                </div>
                <div className="bg-white/[0.03] border border-white/10 rounded-xl p-3">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider">Topics Left</p>
                  <p className="text-xl font-bold text-blue-400">{blogStats.remaining_topics} <span className="text-xs text-gray-500">/ {blogStats.total_topics_available}</span></p>
                </div>
                <div className="bg-white/[0.03] border border-white/10 rounded-xl p-3">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider">Latest Post</p>
                  <p className="text-xs text-white truncate">{blogStats.latest_post?.title || 'None'}</p>
                  {blogStats.latest_post?.published_at && <p className="text-[9px] text-gray-500">{new Date(blogStats.latest_post.published_at).toLocaleDateString()}</p>}
                </div>
              </div>
            )}

            {/* Edit Modal */}
            {editingPost && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setEditingPost(null)}>
                <div className="bg-[#0D1520] border border-white/10 rounded-2xl p-6 max-w-3xl w-full mx-4 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="blog-edit-modal">
                  <h3 className="font-bold text-lg mb-4">Edit Article</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-[10px] text-gray-500 uppercase">Title</label>
                      <Input value={editingPost.title} onChange={e => setEditingPost(p => ({...p, title: e.target.value}))} className="bg-white/5 border-white/10 text-white rounded-lg" data-testid="blog-edit-title" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] text-gray-500 uppercase">Category</label>
                        <Input value={editingPost.category} onChange={e => setEditingPost(p => ({...p, category: e.target.value}))} className="bg-white/5 border-white/10 text-white rounded-lg" />
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-500 uppercase">Tags (comma-separated)</label>
                        <Input value={(editingPost.tags || []).join(', ')} onChange={e => setEditingPost(p => ({...p, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean)}))} className="bg-white/5 border-white/10 text-white rounded-lg" />
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 uppercase">Excerpt</label>
                      <textarea value={editingPost.excerpt || ''} onChange={e => setEditingPost(p => ({...p, excerpt: e.target.value}))} rows={2} className="w-full bg-white/5 border border-white/10 text-white rounded-lg p-2 text-sm resize-none" />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 uppercase">Meta Description (SEO)</label>
                      <Input value={editingPost.meta_description || ''} onChange={e => setEditingPost(p => ({...p, meta_description: e.target.value}))} className="bg-white/5 border-white/10 text-white rounded-lg" />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 uppercase">Content (Markdown)</label>
                      <textarea value={editingPost.content || ''} onChange={e => setEditingPost(p => ({...p, content: e.target.value}))} rows={14} className="w-full bg-white/5 border border-white/10 text-white rounded-lg p-3 text-xs font-mono resize-none" data-testid="blog-edit-content" />
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="text-xs text-gray-400">Published:</label>
                      <button onClick={() => setEditingPost(p => ({...p, published: !p.published}))} className="flex items-center gap-1">
                        {editingPost.published ? <ToggleRight size={24} className="text-emerald-400" weight="fill" /> : <ToggleLeft size={24} className="text-gray-500" weight="fill" />}
                        <span className={`text-xs ${editingPost.published ? 'text-emerald-400' : 'text-gray-500'}`}>{editingPost.published ? 'Yes' : 'No'}</span>
                      </button>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button onClick={handleSaveEdit} className="bg-red-600 hover:bg-red-700 rounded-xl flex-1" data-testid="blog-edit-save">Save Changes</Button>
                      <Button onClick={() => setEditingPost(null)} variant="ghost" className="text-gray-400 rounded-xl">Cancel</Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-3 gap-4">
              {/* AI Generate */}
              <div className="space-y-4">
                <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4">
                  <h3 className="font-bold text-sm mb-3 flex items-center gap-2"><Robot size={16} className="text-[#FF9900]" /> AI Generate Article</h3>
                  <div className="space-y-2">
                    <Input value={blogGenTopic} onChange={e => setBlogGenTopic(e.target.value)} placeholder="Topic (leave blank for auto)" className="bg-white/5 border-white/10 text-white rounded-lg text-xs" data-testid="blog-gen-topic" />
                    <select value={blogGenCategory} onChange={e => setBlogGenCategory(e.target.value)} className="w-full bg-white/5 border border-white/10 text-white text-xs rounded-lg p-2" data-testid="blog-gen-category">
                      <option value="">Auto-select category</option>
                      <option value="Courier Business">Courier Business</option>
                      <option value="Shipping Guides">Shipping Guides</option>
                      <option value="Package Tracking">Package Tracking</option>
                      <option value="Logistics Technology">Logistics Technology</option>
                      <option value="E-commerce Shipping">E-commerce Shipping</option>
                    </select>
                    <Button onClick={handleGenerateArticle} disabled={blogGenerating} className="w-full bg-[#FF9900] hover:bg-[#FF9900]/90 rounded-xl text-xs" data-testid="blog-gen-btn">
                      {blogGenerating ? <><ArrowClockwise size={14} className="animate-spin mr-1" /> Generating...</> : <><Robot size={14} className="mr-1" /> Generate Article</>}
                    </Button>
                  </div>
                </div>

                {/* Manual Create */}
                <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4">
                  <h3 className="font-bold text-sm mb-3 flex items-center gap-2"><Plus size={16} className="text-emerald-400" /> Create Manual Post</h3>
                  <div className="space-y-2">
                    <Input value={manualPostForm.title} onChange={e => setManualPostForm(p => ({...p, title: e.target.value}))} placeholder="Article title" className="bg-white/5 border-white/10 text-white rounded-lg text-xs" data-testid="blog-manual-title" />
                    <select value={manualPostForm.category} onChange={e => setManualPostForm(p => ({...p, category: e.target.value}))} className="w-full bg-white/5 border border-white/10 text-white text-xs rounded-lg p-2">
                      <option value="General">General</option>
                      <option value="Courier Business">Courier Business</option>
                      <option value="Shipping Guides">Shipping Guides</option>
                      <option value="Package Tracking">Package Tracking</option>
                      <option value="Logistics Technology">Logistics Technology</option>
                      <option value="E-commerce Shipping">E-commerce Shipping</option>
                    </select>
                    <Input value={manualPostForm.tags} onChange={e => setManualPostForm(p => ({...p, tags: e.target.value}))} placeholder="Tags (comma-separated)" className="bg-white/5 border-white/10 text-white rounded-lg text-xs" />
                    <textarea value={manualPostForm.content} onChange={e => setManualPostForm(p => ({...p, content: e.target.value}))} placeholder="Article content (markdown)" rows={5} className="w-full bg-white/5 border border-white/10 text-white rounded-lg p-2 text-xs resize-none" data-testid="blog-manual-content" />
                    <Button onClick={handleCreateManualPost} className="w-full bg-emerald-600 hover:bg-emerald-700 rounded-xl text-xs" data-testid="blog-manual-create">
                      <Plus size={14} className="mr-1" /> Create Article
                    </Button>
                  </div>
                </div>
              </div>

              {/* Articles List */}
              <div className="md:col-span-2 bg-white/[0.03] border border-white/10 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-sm">All Articles ({blogTotal})</h3>
                  <Button onClick={() => window.open('/blog', '_blank')} variant="ghost" className="text-xs text-gray-400 hover:text-white" data-testid="blog-view-public">
                    <Eye size={14} className="mr-1" /> View Public Blog
                  </Button>
                </div>
                <div className="space-y-2 max-h-[calc(100vh-320px)] overflow-y-auto" data-testid="blog-posts-list">
                  {blogPosts.map(post => (
                    <div key={post.id} className="p-3 rounded-lg bg-white/[0.02] border border-white/5 hover:border-white/10 transition-colors">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`w-1.5 h-1.5 rounded-full ${post.published ? 'bg-emerald-400' : 'bg-amber-400'}`} />
                            <span className="text-[10px] font-semibold text-[#FF9900] uppercase">{post.category}</span>
                            <span className="text-[9px] text-gray-600">{post.published_at ? new Date(post.published_at).toLocaleDateString() : 'Draft'}</span>
                          </div>
                          <p className="text-sm font-medium text-white truncate">{post.title}</p>
                          <p className="text-[10px] text-gray-500 truncate">{post.excerpt}</p>
                          <div className="flex gap-1 mt-1">
                            {(post.tags || []).slice(0, 3).map(tag => (
                              <span key={tag} className="px-1.5 py-0.5 bg-white/5 text-[8px] text-gray-500 rounded">{tag}</span>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button onClick={() => window.open(`/blog/${post.slug}`, '_blank')} className="p-1.5 rounded-lg hover:bg-white/5 text-gray-500 hover:text-white transition-colors" title="View">
                            <Eye size={14} />
                          </button>
                          <button onClick={() => setEditingPost({...post})} className="p-1.5 rounded-lg hover:bg-white/5 text-gray-500 hover:text-blue-400 transition-colors" title="Edit" data-testid={`blog-edit-${post.id}`}>
                            <PencilSimple size={14} />
                          </button>
                          <button onClick={() => handleTogglePublish(post)} className="p-1.5 rounded-lg hover:bg-white/5 transition-colors" title={post.published ? 'Unpublish' : 'Publish'}>
                            {post.published ? <ToggleRight size={16} className="text-emerald-400" weight="fill" /> : <ToggleLeft size={16} className="text-gray-500" weight="fill" />}
                          </button>
                          <button onClick={() => handleDeletePost(post.id)} className="p-1.5 rounded-lg hover:bg-red-500/10 text-gray-500 hover:text-red-400 transition-colors" title="Delete" data-testid={`blog-delete-${post.id}`}>
                            <Trash size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {blogPosts.length === 0 && <p className="text-xs text-gray-600 text-center py-8">No articles yet. Generate one with AI or create manually.</p>}
                </div>

                {/* Pagination */}
                {blogPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-3 pt-3 border-t border-white/5">
                    <Button disabled={blogPage <= 1} onClick={() => setBlogPage(p => p - 1)} variant="ghost" className="text-xs text-gray-400">Prev</Button>
                    <span className="text-xs text-gray-500">Page {blogPage} / {blogPages}</span>
                    <Button disabled={blogPage >= blogPages} onClick={() => setBlogPage(p => p + 1)} variant="ghost" className="text-xs text-gray-400">Next</Button>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
